import { UserProfileShortPopoverTrigger } from '@modules/user/UserProfileShortPopoverTrigger';
import { Typography, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, StackProps } from '@mui/system';
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { SpeakingAvatar } from 'src/components/SpeakingAvatar/SpeakingAvatar';
import Iconify from 'src/components/iconify/Iconify';
import { useLocales } from 'src/locales';
import { InviteStatus, VoiceChannelMember } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { NavActionButton } from '../Navbar/NavActionButton/NavActionButton';
import { WorkspaceShareMembersAvatar } from '../workspace/WorkspaceShareMembersAvatar';
import { VoiceParticipantsPopover } from './VoiceParticipantsPopover';

const IconBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 16,
  width: 16,
  p: 0.25,
}));

type Props = StackProps & {
  conversationId: string;
  participants: VoiceChannelMember[];
  isConnected: boolean;
  isAdmin: boolean;
};

export const VoiceChannelParticipants = ({
  conversationId,
  participants,
  isConnected,
  isAdmin,
  ...rest
}: Props) => {
  const { user } = useAuthContext();

  const theme = useTheme();
  const navigate = useNavigate();
  const { translate } = useLocales();

  const { activeVoiceChannel, setTheaterMember, myMeetId } =
    useLivekitRTCState();

  const showParticipantScreen = (member: VoiceChannelMember) => {
    if (
      activeVoiceChannel.channel._id !== conversationId ||
      member.meetId === myMeetId
    )
      return;
    setTheaterMember(member.meetId as string);
    navigate(PATH_DASHBOARD.chat.view(conversationId));
  };

  const title: Record<InviteStatus, string> = {
    calling: translate(
      'communicate.voice_channel.invite_status.asking_to_join',
    ),
    ringing: translate('communicate.voice_channel.invite_status.ringing'),
    declined: translate(
      'communicate.voice_channel.invite_status.invite_declined',
    ),
    'not-answered': translate(
      'communicate.voice_channel.invite_status.no_answer',
    ),
    timeout: translate('communicate.voice_channel.invite_status.no_answer'),
    connected: '',
  };

  const colorMap: Record<InviteStatus, string> = {
    calling: theme.palette.success.main,
    ringing: theme.palette.success.main,
    declined: theme.palette.error.main,
    'not-answered': theme.palette.text.secondary,
    timeout: theme.palette.text.secondary,
    connected: theme.palette.text.disabled,
  };

  if (!isConnected) {
    return (
      <Stack {...rest} mb={1} ml={4.5}>
        <WorkspaceShareMembersAvatar
          members={participants.map((item) => ({ user: item.user._id }))}
          size="tiny"
          max={10}
        />
      </Stack>
    );
  }

  return (
    <Stack {...rest}>
      {participants.map((member) => (
        <Fragment key={member.meetId}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              p: 0.75,
              pr: 1,
              '.hoverOnly:not(.active)': {
                visibility: 'hidden',
                width: '0px',
              },
              '&:hover': {
                '.hoverOnly': {
                  visibility: 'visible',
                  width: 'unset',
                },
              },
            }}
          >
            <UserProfileShortPopoverTrigger
              userId={member.user._id}
              key={member.user._id}
            >
              <div>
                <SpeakingAvatar
                  src={member.user.picture}
                  name={member.user.name}
                  alt={member.user.name}
                  meetId={member.meetId}
                  identity={member.identity}
                  size="xSmall"
                  inviteStatus={member.status}
                />
              </div>
            </UserProfileShortPopoverTrigger>
            <Typography
              variant="body2"
              mx={1.5}
              color={
                member.status
                  ? colorMap[member.status]
                  : theme.palette.text.secondary
              }
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              {member.status && member.status !== 'connected'
                ? title[member?.status]
                : member.user.name}
            </Typography>

            <Box sx={{ flexGrow: 1 }} component="span" />

            {isConnected && (
              <>
                {member.muted && (
                  <IconBox>
                    <Iconify
                      icon="tabler:microphone-off"
                      color={theme.palette.text.secondary}
                    />
                  </IconBox>
                )}

                {member.deaf && (
                  <IconBox>
                    <Iconify
                      icon="tabler:headphones-off"
                      color={theme.palette.error.main}
                    />
                  </IconBox>
                )}

                {member.camera && (
                  <IconBox onClick={() => showParticipantScreen(member)}>
                    <Iconify
                      icon="tabler:video"
                      color={theme.palette.success.main}
                    />
                  </IconBox>
                )}

                {member.sharing && (
                  <IconBox onClick={() => showParticipantScreen(member)}>
                    <Iconify
                      icon="tabler:screen-share"
                      color={theme.palette.success.main}
                    />
                  </IconBox>
                )}

                {member.raisedHand && (
                  <IconBox>
                    <Iconify
                      icon="tabler:hand-stop"
                      color={theme.palette.warning.main}
                    />
                  </IconBox>
                )}

                {member?.user?._id !== user?._id && (
                  <Box className="hoverOnly">
                    <PopoverTrigger
                      popover={VoiceParticipantsPopover}
                      context={{
                        channelId: conversationId,
                        member,
                        isAdmin,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <NavActionButton
                        icon="tabler:dots-vertical"
                        color="text.secondary"
                        size={16}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      />
                    </PopoverTrigger>
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Fragment>
      ))}
    </Stack>
  );
};
